import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation } from 'react-router-dom';
import './App.css';

import FeatureLayout from './components/FeatureLayout';
import CustomAuthenticator from './components/CustomAuthenticator';
import LandingPage from './pages/LandingPage';
import DocIngestion from './pages/DocIngestion';
import FileReview from './pages/FileReview';
import DueDiligence from './pages/DueDiligence';
import BorrowerAnalysis from './pages/BorrowerAnalysis';
import TitleSearch from './pages/TitleSearch';
import TitleSearchJobPage from './pages/TitleSearchJobPage';
import WorkflowJobPage from './pages/WorkflowJobPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import TenantListPage from './pages/admin/TenantListPage';
import TenantDetailPage from './pages/admin/TenantDetailPage';
import FilesManagerPage from './pages/admin/FilesManagerPage';
import UserListPage from './pages/admin/UserListPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import PlatformAdminRoute from './components/admin/PlatformAdminRoute';
import TenantAdminRoute from './components/admin/TenantAdminRoute';
import { JobType } from './graphql/types';
import { TitleSearchWorkspace } from './workspace/TitleSearchWorkspace';
import { WorkflowWorkspace } from './workspace/WorkflowWorkspace';
import { DraftWorkspace } from './workspace/DraftWorkspace';
import { isWorkspacePath } from './workspace/modes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantContextProvider } from './contexts/TenantContext';

// Import AWS config
import './aws-config';
import '@aws-amplify/ui-react/styles.css';
import './amplify-ui-overrides.css';

/** Blocks rendering until AuthContext has finished loading the current user. */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

// Check if we're in preview mode or have AWS config
const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                     !import.meta.env.VITE_COGNITO_USER_POOL_ID;

// Mock user for preview/build
const mockUser = {
  username: 'preview-user',
  signInDetails: {
    loginId: 'preview@docmind.com'
  }
};

const mockSignOut = () => {
  console.log('Sign out clicked (preview mode)');
};

/**
 * Frame for every route. Workspace routes (`/:feature/:jobId`) render full-bleed
 * without the Feature sidebar layout; all other routes use `FeatureLayout`.
 * Requirements: 3.1, 2.1
 */
const AppFrame: React.FC<{ user: any; signOut: () => void; children: React.ReactNode }> = ({
  user,
  signOut,
  children,
}) => {
  const { pathname } = useLocation();
  if (isWorkspacePath(pathname)) {
    return <>{children}</>;
  }
  return (
    <FeatureLayout user={user} signOut={signOut}>
      {children}
    </FeatureLayout>
  );
};

/**
 * Wrapper component for Due Diligence job page
 * Extracts jobId from URL params and passes JobType.DD.
 * The Workspace container loads the job; the legacy page is hosted as its body
 * until the Mode bodies (tasks 4.x, 5.x, 6.x) replace it.
 * Requirements: 11.3, 3.1
 */
const DueDiligenceJobPageWrapper: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  return (
    <WorkflowWorkspace jobId={jobId!} jobType={JobType.DD}>
      <WorkflowJobPage />
    </WorkflowWorkspace>
  );
};

/**
 * Wrapper component for Borrower Analysis job page
 * Extracts jobId from URL params and passes JobType.BA.
 * Requirements: 11.4, 3.1
 */
const BorrowerAnalysisJobPageWrapper: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  return (
    <WorkflowWorkspace jobId={jobId!} jobType={JobType.BA}>
      <WorkflowJobPage />
    </WorkflowWorkspace>
  );
};

/**
 * Wrapper component for Title Search job page
 * Extracts jobId from URL params. The Workspace container loads the job; the
 * legacy page is hosted as its body until the Mode bodies (tasks 7 to 9)
 * replace it.
 * Requirements: 8.4, 8.5, 3.1
 */
const TitleSearchJobPageWrapper: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  return (
    <TitleSearchWorkspace jobId={jobId!}>
      <TitleSearchJobPage />
    </TitleSearchWorkspace>
  );
};

function App() {
  if (isPreviewMode) {
    // Preview mode without AWS dependencies
    return (
        <AuthProvider>
          <AuthGate>
          <TenantContextProvider>
          <Router>
            <AppFrame user={mockUser} signOut={mockSignOut}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/doc-ingestion" element={<DocIngestion />} />
                <Route path="/file-review" element={<FileReview />} />
                {/* Due Diligence routes - Requirements: 11.3 */}
                <Route path="/due-diligence" element={<DueDiligence />} />
                <Route path="/due-diligence/new" element={<DraftWorkspace feature={JobType.DD} />} />
                <Route path="/due-diligence/:jobId" element={<DueDiligenceJobPageWrapper />} />
                {/* Borrower Analysis routes - Requirements: 11.4 */}
                <Route path="/borrower-analysis" element={<BorrowerAnalysis />} />
                <Route path="/borrower-analysis/new" element={<DraftWorkspace feature={JobType.BA} />} />
                <Route path="/borrower-analysis/:jobId" element={<BorrowerAnalysisJobPageWrapper />} />
                {/* Title Search routes - Requirements: 8.4, 8.5 */}
                <Route path="/title-search" element={<TitleSearch />} />
                <Route path="/title-search/new" element={<DraftWorkspace feature={JobType.TR} />} />
                <Route path="/title-search/:jobId" element={<TitleSearchJobPageWrapper />} />
                {/* Platform Admin routes - Requirements: 10.1, 10.3, 10.5 */}
                <Route path="/platform-admin/tenants" element={
                  <PlatformAdminRoute>
                    <TenantListPage />
                  </PlatformAdminRoute>
                } />
                <Route path="/platform-admin/tenants/:tenantId" element={
                  <PlatformAdminRoute>
                    <TenantDetailPage />
                  </PlatformAdminRoute>
                } />
                <Route path="/platform-admin/files-manager" element={
                  <PlatformAdminRoute>
                    <FilesManagerPage />
                  </PlatformAdminRoute>
                } />
                {/* Tenant Admin routes - Requirements: 11.1, 11.3, 11.4, 11.5 */}
                <Route path="/admin/users" element={
                  <TenantAdminRoute>
                    <UserListPage />
                  </TenantAdminRoute>
                } />
                <Route path="/admin/users/:userId" element={
                  <TenantAdminRoute>
                    <UserDetailPage />
                  </TenantAdminRoute>
                } />
                {/* Unauthorized page */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
              </Routes>
            </AppFrame>
          </Router>
          </TenantContextProvider>
          </AuthGate>
        </AuthProvider>
    );
  }

  // Production mode with AWS Amplify
  return (
      <CustomAuthenticator>
        {({ signOut, user }: any) => (
          <AuthProvider>
            <AuthGate>
            <TenantContextProvider>
            <Router>
              <AppFrame user={user} signOut={signOut}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/doc-ingestion" element={<DocIngestion />} />
                  <Route path="/file-review" element={<FileReview />} />
                  {/* Due Diligence routes - Requirements: 11.3 */}
                  <Route path="/due-diligence" element={<DueDiligence />} />
                  <Route path="/due-diligence/new" element={<DraftWorkspace feature={JobType.DD} />} />
                  <Route path="/due-diligence/:jobId" element={<DueDiligenceJobPageWrapper />} />
                  {/* Borrower Analysis routes - Requirements: 11.4 */}
                  <Route path="/borrower-analysis" element={<BorrowerAnalysis />} />
                  <Route path="/borrower-analysis/new" element={<DraftWorkspace feature={JobType.BA} />} />
                  <Route path="/borrower-analysis/:jobId" element={<BorrowerAnalysisJobPageWrapper />} />
                  {/* Title Search routes - Requirements: 8.4, 8.5 */}
                  <Route path="/title-search" element={<TitleSearch />} />
                  <Route path="/title-search/new" element={<DraftWorkspace feature={JobType.TR} />} />
                  <Route path="/title-search/:jobId" element={<TitleSearchJobPageWrapper />} />
                  {/* Platform Admin routes - Requirements: 10.1, 10.3, 10.5 */}
                  <Route path="/platform-admin/tenants" element={
                    <PlatformAdminRoute>
                      <TenantListPage />
                    </PlatformAdminRoute>
                  } />
                  <Route path="/platform-admin/tenants/:tenantId" element={
                    <PlatformAdminRoute>
                      <TenantDetailPage />
                    </PlatformAdminRoute>
                  } />
                  <Route path="/platform-admin/files-manager" element={
                    <PlatformAdminRoute>
                      <FilesManagerPage />
                    </PlatformAdminRoute>
                  } />
                  {/* Tenant Admin routes - Requirements: 11.1, 11.3, 11.4, 11.5 */}
                  <Route path="/admin/users" element={
                    <TenantAdminRoute>
                      <UserListPage />
                    </TenantAdminRoute>
                  } />
                  <Route path="/admin/users/:userId" element={
                    <TenantAdminRoute>
                      <UserDetailPage />
                    </TenantAdminRoute>
                  } />
                  {/* Unauthorized page */}
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                </Routes>
              </AppFrame>
            </Router>
            </TenantContextProvider>
            </AuthGate>
          </AuthProvider>
        )}
      </CustomAuthenticator>
  );
}

export default App;